import { NO_VIDEO_GREY_BG } from '@anm/constants/colors';
import hex2rgba from '@anm/helpers/color/hex2rgba';
import getFromCdn from '@anm/helpers/getFromCdn';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import getThumbnailHeight from '../../helpers/getThumbnailHeight';
import { stretchBlock, blueFilter } from '@anm/styles/helpers';
import plyrDefaultStyles from '@anm/styles/third-party/plyr';
import styled, { css } from 'styled-components';

import getPlayIconSize from '../../helpers/getPlayIconSize';

type PlayerWrapperProps = {
  isNoVideo: boolean;
  isVertical: boolean;
  showDefaultPreview: boolean;
  playerWidth?: number;
  hideControls?: boolean;
  thumbnailWidth?: number;
  canCopyTimeLink?: boolean;
};

export const Preview = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  pointer-events: none;
  bottom: 0;
  z-index: 1;
  width: 100%;
  user-select: none;
`;

const noVideoStyles = css`
  .plyr__control[data-plyr='play']:not(.plyr__control--overlaid) {
    pointer-events: none;
  }

  &:after {
    ${stretchBlock};
    z-index: 1;
    content: '';
    background: rgba(41, 42, 43, 0.5);
  }
`;

const noPreviewStyles = css`
  background: url(${getFromCdn('images/dashboard/default_preview.svg')}) no-repeat center/20%, ${NO_VIDEO_GREY_BG};
`;

const volumeStyles = css`
  .plyr__volume input[type='range'] {
    bottom: 100%;
    position: absolute;
    background: #fff;
    border-radius: 0px;
    padding: 0 12px;
    width: 96px;
    height: 32px;
    left: 100%;
    transform: rotate(-90deg);
    transform-origin: 0px 100%;
    margin: 0px;
    cursor: pointer;
    z-index: 3;
    opacity: 0;
    visibility: hidden;
    transition: ease-out opacity 0.2s;
    display: block;
  }

  .plyr__volume:hover {
    input[type='range'] {
      opacity: 1;
      visibility: visible;
    }
  }

  .plyr__volume input[type='range']::-webkit-slider-runnable-track {
    max-height: 4px;
    background-color: #000;
    color: rgba(0, 0, 0, 0.2);
    background-image: linear-gradient(to right, currentColor var(--value, 0), currentColor var(--value, 0)) !important;
  }

  .plyr__volume input[type='range'] {
    ::-webkit-slider-thumb {
      width: 10px;
      height: 10px;
      background-color: #000;
      background: #000;
      color: #000;
      margin-top: -3px;
    }
  }

  .plyr__volume input[type='range'] {
    ::-moz-range-thumb {
      background-color: #000;
    }
  }
`;

export const PlayerWrapper = styled.div<{ playerColor: string }>`
  .plyr__controls,
  .plyr__control--overlaid {
    background-color: ${({ playerColor }) => hex2rgba(playerColor, 0.75)} !important;
  }
`;

const Wrapper = styled.div<PlayerWrapperProps>`
  ${plyrDefaultStyles};

  margin: 0 auto;
  position: relative;
  overflow: hidden;
  ${({ isNoVideo, showDefaultPreview }) =>
    isNoVideo && showDefaultPreview ? noPreviewStyles : isNoVideo ? noVideoStyles : null};

  .plyr__controls,
  .plyr__control--overlaid {
    ${({ hideControls }) =>
      hideControls &&
      css`
        opacity: 0;
      `}
  }

  .plyr__volume input[type='range'] {
    display: none;
  }

  ${!isMobileOrIPad() && volumeStyles};

  video {
    max-width: 100%;
  }

  .plyr__poster {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    display: none;
  }

  .plyr__controls__item:not(.plyr__time):not(.plyr__progress__container) {
    min-width: 32px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  }

  .plyr__control[data-plyr='play']:not(.plyr__control--overlaid) {
    box-shadow: none;
    margin: 0 !important;
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;

    svg {
      width: 14.13px;
      height: 12.5px;
    }
  }

  .plyr__control[data-plyr='fullscreen'] {
    margin: 0;

    svg {
      width: 12px;
      height: 12px;
    }

    &:before {
      display: block;
    }
  }

  .plyr__control.plyr__control--overlaid {
    width: ${({ playerWidth }) => `${getPlayIconSize(playerWidth || 1)}px`};
    height: ${({ playerWidth }) => `${getPlayIconSize(playerWidth || 1)}px`};
    border-radius: 0;
    padding: 0;
    display: flex !important;
    box-shadow: none;
    transition: none;
    transform: translate(-50%, calc(-50% - 10px));
    border-radius: 50%;

    svg {
      display: block;
      margin: auto;
      left: unset;
      transform: translateX(8%);
      width: 38.414%;
      height: 36.428%;
      transition: 0.3s;
    }
  }

  .plyr__control[data-plyr='settings'] {
    margin-left: 0;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .plyr__control--forward {
    transition: 0.3s;

    & > span {
      font-weight: bold;
      color: #292a2b;

      span {
        font-weight: normal;
      }
    }

    &:hover {
      background: rgba(41, 42, 43, 0.1);
    }
  }

  .plyr__control[data-plyr='mute'] {
    svg {
      width: 17.71px;
      height: 16px;
    }

    &:before {
      display: block;
      content: '';
      width: 30px;
      height: 30px;
      position: absolute;
      top: -30px;
      left: 0;
      z-index: 2;
    }
  }

  .plyr__menu__container .plyr__control > span .plyr--video,
  .plyr__video-wrapper,
  .plyr__control[aria-expanded='true'],
  .plyr--video,
  .plyr--video .plyr--video .plyr__control.plyr__tab-focus,
  .plyr--video .plyr__control:hover,
  .plyr--video .plyr__control[aria-expanded='true'],
  .plyr__controls > .plyr__control:hover {
    background: transparent;
  }

  .plyr__controls .plyr__controls__item.plyr__volume {
    padding: 0;
    max-width: 32px !important;
    margin: 0;
    flex-grow: 0;
    position: relative;
  }

  /* plyr mute icon bug - https://github.com/sampotts/plyr/issues/1543 */
  .plyr__controls .plyr__controls__item.plyr__volume[hidden]:not(.hidden) {
    display: flex !important;
  }

  .plyr__control[data-plyr='mute'][hidden] {
    display: inherit !important;
  }

  .plyr__controls .plyr__controls__item {
    margin-left: 0;
  }

  .plyr__controls__item {
    svg {
      transition: 0.3s;
    }
  }

  .plyr__menu__container .plyr__control > span {
    color: #292a2b;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-style: normal;
    font-family: Inter;
    font-size: 14px;
    letter-spacing: 0.01em;
    color: #292a2b;
  }

  .plyr__menu__container [role='menu'] {
    padding: 10px 0;
    border-radius: 0;
  }

  .plyr--full-ui input[type='range']:not([data-plyr='volume']) {
    height: 2px;
  }

  .plyr__control[data-plyr='mute']:before {
    display: none;
  }

  input[type='range']::-webkit-slider-runnable-track {
    height: 100%;
    max-height: 2px;
    transition: 0.1s ease-out !important;
  }

  .plyr__controls .plyr__controls__item.plyr__progress__container {
    position: absolute;
    top: -11px;
    margin: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 0;

    &:hover {
      input[type='range']::-webkit-slider-runnable-track {
        max-height: 4px;
      }

      input[type='range']:not([data-plyr='volume'])::-webkit-slider-thumb {
        transform: translateY(-4.5px);
      }
    }
  }

  .plyr__menu {
    position: static;
  }

  .plyr__menu__container {
    background: #fff;
    color: #292a2b;
    border-radius: 0;
    right: 0;
    margin-bottom: 0;
    box-shadow: none;
    min-width: 96px;

    &:after {
      display: none;
    }

    .plyr__menu__value {
      padding-left: 0;
      margin-right: 0;
      margin-left: 10px;
      text-align: left;
      display: block;
    }
  }

  .plyr__menu__container {
    .plyr__control {
      &:after {
        display: block;
        width: 10px;
        height: 6px;
        content: '';
        background: url(${getFromCdn('images/icons/dropdown_arrow.svg')}) no-repeat center;
        position: absolute;
        top: 50%;
        transform: translateY(-50%) rotate(-180deg);
        right: 15px;
        border: 0;
      }

      &[role='menuitemradio'] {
        &:after {
          content: '';
        }
      }
    }

    .plyr__control--back {
      &:after {
        right: auto;
        transform: translateY(-50%) rotate(0);
        left: 12px;
      }

      span {
        font-weight: bold;
      }
    }
  }

  .plyr__progress {
    left: 0;
    margin-right: 0;

    &:focus {
      outline: none;
    }

    &:hover {
      cursor: pointer;

      input[type='range'] {
        cursor: pointer;
      }
    }
  }

  progress {
    opacity: 0;
  }

  .plyr--full-ui input[type='range']:not([data-plyr='volume'])::-webkit-slider-thumb {
    width: 12px;
    height: 12px;
    transition: 0.2s;
    transform: translateY(-5px);
    margin-top: 0px;
    box-shadow: none;
  }

  .plyr__progress input[type='range']:not([data-plyr='volume']),
  .plyr__progress__buffer {
    margin: 0;
    height: 2px;
    width: 100%;
    padding: 10px 0 0 0;
    transition: 0.3s;
  }

  .plyr__controls .plyr__controls__item.plyr__time {
    margin-right: auto;
    padding: 0 0 0 34px;
    margin-left: 5px;
    font-weight: bold;
    font-size: 14px;
    line-height: 17px;
    text-shadow: none;
  }

  .plyr--video .plyr__progress__buffer {
    box-shadow: none;
    top: 0;
  }

  .plyr--full-ui input[type='range'].plyr__tab-focus::-webkit-slider-runnable-track,
  .plyr--full-ui input[type='range']::-webkit-slider-thumb {
    box-shadow: none;
  }

  .plyr--full-ui.plyr--video input[type='range']::-webkit-slider-runnable-track {
    border-radius: 0;
    background-image: linear-gradient(to right, currentColor var(--value, 0), rgba(255, 255, 255, 0.5) var(--value, 0));
  }

  .plyr--full-ui input[type='range'].plyr__tab-focus::-webkit-slider-runnable-track {
    box-shadow: none;
    border-radius: 0;
  }

  .plyr--video .plyr__control svg {
    filter: none;
  }

  .plyr__control--overlaid {
    background: transparent;
    overflow: hidden;
  }

  .plyr__control.plyr__tab-focus {
    box-shadow: none;
  }

  .plyr--video .plyr__controls {
    padding: 0;
    min-height: 36px;
    background: transparent;
  }

  .plyr--video .plyr__menu__container .plyr__control[data-plyr='settings']:hover,
  .plyr__control[role='menuitemradio']:hover {
    background: rgba(41, 42, 43, 0.1);
  }

  .plyr__badge {
    display: none;
  }

  .plyr__menu__container .plyr__control[role='menuitemradio']:after {
    display: none;
  }

  .plyr__menu__container .plyr__control[role='menuitemradio'].plyr__tab-focus::before,
  .plyr__menu__container .plyr__control[role='menuitemradio']:hover::before {
    background: transparent;
  }

  .plyr__menu__container .plyr__control[role='menuitemradio']::before {
    background: transparent;
    width: 10px;
    height: 7px;
    margin: 0 7px 0 14.42px;
  }

  .plyr__menu__container .plyr__control[role='menuitemradio'][aria-checked='true']::before {
    background: url(${getFromCdn('images/icons/check_mark.svg')}) no-repeat center / cover;
  }

  .plyr__controls > .plyr__control:hover,
  .plyr__controls > .plyr__volume:hover,
  .plyr__controls > .plyr__menu:hover {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 0;
  }

  .plyr__menu__container .plyr__control--back {
    margin: 10px 0 0 0;
    width: 100%;
    padding: 0 18px 0 31px;
  }

  .plyr__menu__container .plyr__control--back:before {
    display: none;
  }

  .plyr__menu__container .plyr__control--back + [role='menu'] {
    padding-top: 0;
  }

  .plyr__control[data-plyr='settings'] {
    &[role='menuitem'] {
      padding: 4.5px 31px 4.5px 18px;
    }
  }

  .plyr__controls .plyr__controls__item:hover svg,
  .plyr__control[data-plyr='fullscreen']:hover svg,
  .plyr__control[data-plyr='play']:not(.plyr__control--overlaid):hover svg,
  .plyr__control.plyr__control--overlaid:hover svg {
    opacity: 0.85;
  }

  .plyr__preview-thumb {
    border-radius: 0;
    background: transparent;
    padding: 0;
    box-shadow: none;
    bottom: 30px;
    transform: none;
    margin-bottom: 0;

    a.vertical {
      width: 15px;
      height: 15px;
      background: url(${getFromCdn('images/icons/icon_link.svg')}) no-repeat center / contain;

      &:hover {
        ${blueFilter};
      }
    }
  }

  .plyr__progress:hover {
    .plyr__preview-thumb {
      pointer-events: unset;

      &:hover {
        opacity: 1;
      }
    }

    .plyr__preview-thumb__image-container:empty + div {
      opacity: 0;
    }
  }

  .plyr__preview-thumb:before {
    display: none;
  }

  .plyr__preview-thumb__image-container {
    border-radius: 0;
    background: transparent;
    width: ${({ thumbnailWidth }) => `${thumbnailWidth}px` || 'auto'};
    height: ${({ isVertical }) => `${getThumbnailHeight(isVertical)}px`};
  }

  .plyr__preview-thumb__time-container {
    width: 100%;
    background: #fff;
    text-align: center;
    bottom: -20px;
    height: 20px;
    line-height: 20px;

    ${({ canCopyTimeLink }) =>
      canCopyTimeLink &&
      css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 5px;
      `}

    &.copied {
      justify-content: center;
      & > * {
        display: none;
      }

      &:after {
        display: block;
        color: #30aef2;
        content: 'Copied!';
      }
    }
  }

  .plyr__preview-thumb__time-container span,
  .plyr__preview-thumb__time-container a,
  .plyr__preview-thumb__time-container:after {
    background: transparent;
    font-size: 10px;
    letter-spacing: 0.01em;
    color: #292a2b;
    font-weight: bold;
    padding: 0;
  }

  .plyr__preview-thumb__time-container a:hover,
  .plyr__preview-thumb__time-container a.active {
    color: #30aef2;
  }

  .plyr--full-ui input[type='range']:not([data-plyr='volume']) {
    color: #fff;
  }

  .plyr--full-ui input[type='range'][data-plyr='volume'] {
    color: rgba(0, 0, 0, 0.2);
  }

  *:not(.plyr__controls).hidden {
    display: none !important;
  }

  .plyr__control[data-plyr='play']:not(.plyr__control--overlaid).hidden {
    & ~ .plyr__controls__item.plyr__time {
      padding: 0 0 0 10px;
    }
  }

  .plyr__controls.hidden {
    background-color: transparent !important;
    backdrop-filter: none;
    background: linear-gradient(180deg, rgba(41, 42, 43, 0) 0%, rgba(41, 42, 43, 0.4) 100%);

    .plyr__controls__item.plyr__progress__container {
      display: none;
    }
  }

  .plyr__controls.disabled {
    display: none;
  }

  .plyr--video .plyr__control.plyr__tab-focus,
  .plyr--video .plyr__control[aria-expanded='true'] {
    background: inherit;
  }

  .plyr__menu__container .plyr__control[role='menuitemradio'] {
    padding-left: 0;
  }

  .plyr__preview-scrubbing {
    display: none;
  }

  .plyr__control {
    padding: 0;
    border-radius: unset;
  }

  .plyr {
    &.disabled {
      pointer-events: none;
    }
  }
`;

export default Wrapper;
